import { useCharacterStore } from '@/state/characterStore';
import { useFieldStore } from '@/state/fieldStore';
import mapsData from '@/data/maps.json';
import npcsData from '@/data/npcs.json';
import monstersData from '@/data/monsters.json';

interface MapDef { id: number; key: string; name: string; type: string; spawnX?: number; spawnY?: number }
interface NpcDef { id: string; name: string; role: string; map: string; x: number; y: number; color: string }
interface MonsterTpl { tplId: number; name: string; level: number }

const MONSTER_TPLS: Record<number, MonsterTpl> = Object.fromEntries(
  (monstersData.monsters as MonsterTpl[]).map((m) => [m.tplId, m])
);

const roleColor = (role: string) => {
  switch (role) {
    case 'quest': return '#f5c842';
    case 'shop': return '#e74c3c';
    case 'guild': return '#c27aff';
    default: return '#8899aa';
  }
};

const frameStyle: React.CSSProperties = {
  position: 'absolute',
  top: 8,
  right: 8,
  width: 180,
  height: 180,
  pointerEvents: 'none',
  background: 'linear-gradient(180deg, #1a1a2e 0%, #0f0f1a 100%)',
  border: '3px solid #3a2f1e',
  borderRadius: 4,
  boxShadow:
    'inset 0 0 8px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6), inset 0 1px 0 #5a4a30, inset 0 -1px 0 #2a1f10',
  outline: '2px solid #0a0a12',
};

const innerBorderStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 4,
  border: '1px solid #5a4a30',
  borderRadius: 2,
  boxShadow: 'inset 0 0 4px rgba(90,74,48,0.3)',
  pointerEvents: 'none',
  zIndex: 3,
};

const cornerSize = 8;
const cornerBase: React.CSSProperties = {
  position: 'absolute',
  width: cornerSize,
  height: cornerSize,
  zIndex: 4,
  pointerEvents: 'none',
};

const cornerGradient = 'linear-gradient(135deg, #8a7a50 0%, #5a4a30 50%, #3a2f1e 100%)';

export function MiniMap() {
  const ch = useCharacterStore((s) => s.current);
  const liveMonsters = useFieldStore((s) => s.monsters);
  const map = (mapsData.maps as MapDef[]).find((m) => m.id === ch?.mapId);
  const npcs = (npcsData.npcs as NpcDef[]).filter((n) => n.map === map?.key);

  const toPct = (x: number, y: number) => ({
    left: `${50 + (x - 640) / 32}%`,
    top: `${50 + (y - 360) / 32}%`,
  });

  return (
    <div style={frameStyle}>
      {/* Inner gold border */}
      <div style={innerBorderStyle} />

      {/* Corner ornaments */}
      <div style={{ ...cornerBase, top: 1, left: 1, background: cornerGradient, borderRadius: '3px 0 0 0' }} />
      <div style={{ ...cornerBase, top: 1, right: 1, background: cornerGradient, borderRadius: '0 3px 0 0' }} />
      <div style={{ ...cornerBase, bottom: 1, left: 1, background: cornerGradient, borderRadius: '0 0 0 3px' }} />
      <div style={{ ...cornerBase, bottom: 1, right: 1, background: cornerGradient, borderRadius: '0 0 3px 0' }} />

      {/* Map title */}
      <div style={{
        position: 'absolute',
        top: 4,
        left: 10,
        right: 10,
        textAlign: 'center',
        zIndex: 5,
        fontSize: 10,
        fontWeight: 700,
        color: '#d4a843',
        textShadow: '0 0 4px rgba(212,168,67,0.5), 1px 1px 1px #000',
        fontFamily: '"Philosopher", "Marcellus", serif',
        letterSpacing: 1,
      }}>
        {map?.name ?? '\u2014'}
      </div>

      {/* Coordinates */}
      <div style={{
        position: 'absolute',
        bottom: 4,
        left: 10,
        right: 10,
        textAlign: 'center',
        zIndex: 5,
        fontSize: 9,
        color: '#8899aa',
        textShadow: '1px 1px 1px #000',
        fontFamily: 'monospace',
      }}>
        {ch ? `${Math.round(ch.posX)}, ${Math.round(ch.posY)}` : '\u2014'}
      </div>

      {/* Radar area */}
      <div style={{
        position: 'absolute',
        top: 18,
        left: 8,
        right: 8,
        bottom: 18,
        background: 'radial-gradient(ellipse at center, #0a1628 0%, #060d18 70%, #030810 100%)',
        overflow: 'hidden',
        zIndex: 1,
        border: '1px solid #1a2a3e',
      }}>
        {/* Grid lines */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(30,60,90,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(30,60,90,0.12) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }} />

        {/* NPC dots */}
        {npcs.map((n) => (
          <div
            key={n.id}
            title={n.name}
            style={{
              position: 'absolute',
              ...toPct(n.x, n.y),
              width: 5,
              height: 5,
              background: roleColor(n.role),
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              boxShadow: `0 0 3px ${roleColor(n.role)}`,
            }}
          />
        ))}

        {/* Monster dots */}
        {liveMonsters.map((m) => {
          const tpl = MONSTER_TPLS[m.tplId];
          return (
            <div
              key={m.id}
              title={tpl ? `${tpl.name} Lv.${tpl.level}` : `Monster #${m.tplId}`}
              style={{
                position: 'absolute',
                ...toPct(m.x, m.y),
                width: 4,
                height: 4,
                background: '#ff4444',
                border: '1px solid #ff8888',
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                boxShadow: '0 0 3px #ff4444',
              }}
            />
          );
        })}

        {/* Player dot */}
        <div style={{
          position: 'absolute',
          ...toPct(ch?.posX ?? 0, ch?.posY ?? 0),
          width: 6,
          height: 6,
          background: '#ffd166',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          boxShadow: '0 0 6px #ffd166, 0 0 2px #fff',
          zIndex: 1,
        }} />
      </div>
    </div>
  );
}
